import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Sparkles, X, MessageSquare, Bot, CheckCircle, AlertCircle, FileText } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Per-company AI Assistant — wraps the prompt UI in either:
 *   - <CompanyAIAssistantPanel /> = full page or sidebar embed
 *   - <CompanyAIAssistantBubble /> = floating chat bubble (bottom-right)
 *
 * Both share <AIChat />.
 */

export const AIChat = ({ companyId, companyName, companyCode, onCompanyUpdate }) => {
  const [history, setHistory] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const fetchHistory = async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`${API}/super-admin/companies/${companyId}/ai-assist/history`);
      setHistory(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchHistory();
  }, [companyId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  const send = async () => {
    if (!prompt.trim() || loading) return;
    const userMsg = prompt.trim();
    setPrompt("");
    setLoading(true);
    setHistory(h => [...h, { id: `tmp-${Date.now()}`, prompt: userMsg, pending: true, created_at: new Date().toISOString() }]);
    try {
      const res = await axios.post(`${API}/super-admin/companies/${companyId}/ai-assist`, { prompt: userMsg });
      const data = res.data || {};
      setHistory(h => [
        ...h.filter(m => !m.pending),
        {
          id: `r-${Date.now()}`,
          prompt: userMsg,
          response_parsed: { intent: data.intent, summary: data.summary, answer: data.answer },
          execution: data.execution,
          proposal: data.proposal,
          change_request_id: data.change_request_id,
          billable_usd: data.billable_usd,
          raw_cost_usd: data.raw_cost_usd,
          created_at: new Date().toISOString(),
        },
      ]);
      if (data.proposal) {
        toast.info(`Action staged — review and Apply to bill $${data.billable_usd.toFixed(2)}`);
      } else if (data.change_request_id) {
        toast.success("Change request queued — open the drawer to send for payment");
      } else if (data.intent === "answer") {
        toast.success("Answered");
      }
    } catch (e) {
      setHistory(h => h.filter(m => !m.pending));
      toast.error(e.response?.data?.detail || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const applyProposal = async (msg) => {
    if (!msg.proposal?.id) return;
    if (!window.confirm(`Apply this action and bill ${companyCode} $${msg.billable_usd?.toFixed(2)}?`)) return;
    try {
      const res = await axios.post(`${API}/super-admin/ai-proposals/${msg.proposal.id}/apply`);
      toast.success(res.data.execution?.message || "Applied & billed");
      // mark this msg's proposal as applied locally
      setHistory(h => h.map(m => m.id === msg.id ? { ...m, proposal: { ...m.proposal, status: "applied" }, execution: res.data.execution } : m));
      if (onCompanyUpdate) onCompanyUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    }
  };

  const discardProposal = async (msg) => {
    if (!msg.proposal?.id) return;
    try {
      await axios.post(`${API}/super-admin/ai-proposals/${msg.proposal.id}/discard`);
      toast.success("Discarded");
      setHistory(h => h.map(m => m.id === msg.id ? { ...m, proposal: { ...m.proposal, status: "discarded" } } : m));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discard failed");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="ai-chat-history">
        {history.length === 0 && !loading && (
          <div className="text-center py-10 text-slate-400 text-sm">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-orange-400" />
            <p className="font-semibold">AI Assistant for {companyName}</p>
            <p className="text-xs mt-1">Ask me to update settings, toggle tabs, run reports, or spec a new feature.</p>
            <div className="mt-4 text-xs text-slate-500 space-y-1 max-w-sm mx-auto">
              <div className="bg-white p-2 rounded border border-slate-200">"Change the timezone to Australia/Brisbane"</div>
              <div className="bg-white p-2 rounded border border-slate-200">"How many active jobs do they have?"</div>
              <div className="bg-white p-2 rounded border border-slate-200">"Build a new Site Diary tab for daily notes"</div>
            </div>
          </div>
        )}
        {history.length > 0 && (
          <div className="flex justify-end mb-2">
            <button
              onClick={async () => {
                const transcript = history.map(m => `User: ${m.prompt}\nAI: ${m.response_parsed?.summary || m.response_parsed?.answer || '(staged)'}\n`).join("\n");
                const ctx = `Build request for ${companyName} (${companyCode}, company_id: ${companyId})\n\nFull conversation so far:\n\n${transcript}\n\n--- Please pick this up where the AI got stuck ---`;
                try {
                  await navigator.clipboard.writeText(ctx);
                  toast.success("Context copied — opening Emergent build chat. Paste with Ctrl/⌘+V.");
                } catch (e) {
                  window.prompt("Copy this, then paste into Emergent:", ctx);
                }
                window.open("https://app.emergent.sh", "_blank", "noopener,noreferrer");
              }}
              className="text-[10px] uppercase font-bold tracking-wide px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded flex items-center gap-1"
              data-testid="escalate-to-emergent-btn"
              title="Copy full conversation context and open the Emergent build chat in a new tab"
            >
              ↗ Escalate to Emergent
            </button>
          </div>
        )}
        {history.map(msg => (
          <div key={msg.id} className="space-y-2">
            <div className="flex justify-end">
              <div className="bg-orange-500 text-white rounded-lg rounded-tr-sm px-3 py-2 max-w-[85%] text-sm" data-testid="ai-user-msg">
                {msg.prompt}
              </div>
            </div>
            {msg.pending ? (
              <div className="flex justify-start">
                <div className="bg-white border-2 border-slate-200 rounded-lg rounded-tl-sm px-3 py-2 text-sm text-slate-500 italic">
                  Thinking…
                </div>
              </div>
            ) : (
              <div className="flex justify-start">
                <div className="bg-white border-2 border-slate-200 rounded-lg rounded-tl-sm px-3 py-2 max-w-[85%] text-sm space-y-2" data-testid="ai-bot-msg">
                  <div className="flex items-start gap-2">
                    <Bot className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-slate-700">{msg.response_parsed?.summary || msg.response_parsed?.answer}</div>
                      {msg.response_parsed?.answer && msg.response_parsed?.intent === "answer" && msg.response_parsed.answer !== msg.response_parsed.summary && (
                        <div className="mt-2 whitespace-pre-wrap text-slate-700">{msg.response_parsed.answer}</div>
                      )}
                    </div>
                  </div>
                  {msg.proposal && msg.proposal.status === "pending_payment" && (
                    <div className="mt-2 border-2 border-orange-300 rounded p-2 bg-orange-50">
                      <div className="text-xs text-orange-800 font-semibold mb-2">
                        ⚠ STAGED — billable ${msg.billable_usd?.toFixed(2)} (covers ${msg.raw_cost_usd?.toFixed(2)} + 100% labour)
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => applyProposal(msg)}
                          className="flex-1 text-[11px] px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded font-bold"
                          data-testid={`apply-proposal-${msg.proposal.id}`}
                        >
                          Apply & Bill
                        </button>
                        <button
                          onClick={() => discardProposal(msg)}
                          className="flex-1 text-[11px] px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded font-semibold"
                          data-testid={`discard-proposal-${msg.proposal.id}`}
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  )}
                  {msg.proposal && msg.proposal.status === "applied" && (
                    <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded font-semibold">✓ Applied & billed ${msg.billable_usd?.toFixed(2)}</div>
                  )}
                  {msg.proposal && msg.proposal.status === "discarded" && (
                    <div className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded font-semibold">Discarded — not billed</div>
                  )}
                  {msg.execution && msg.proposal?.status !== "pending_payment" && (
                    <div className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${msg.execution.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                      {msg.execution.ok ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      <span>{msg.execution.message}</span>
                    </div>
                  )}
                  {msg.change_request_id && (
                    <div className="border-2 border-purple-300 rounded p-2 bg-purple-50 space-y-2">
                      <div className="flex items-start gap-2 text-xs text-purple-800">
                        <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span><strong>This needs code changes — I can't write code directly.</strong> Click below and I'll copy the spec + open the Emergent build chat. Just paste (Ctrl/⌘+V) when it opens.</span>
                      </div>
                      <button
                        onClick={async () => {
                          const spec = msg.response_parsed?.change_request?.prompt || msg.response_parsed?.summary || msg.prompt;
                          const ctx = `Build request for ${companyName} (${companyCode}, company_id: ${companyId}):\n\n${spec}\n\nOriginally requested via in-app AI Assistant on ${new Date(msg.created_at).toLocaleString()}.\n\nPlease build this end-to-end (backend + frontend + test) and let me know when to redeploy.`;
                          try {
                            await navigator.clipboard.writeText(ctx);
                            toast.success("Spec copied — opening Emergent build chat. Paste with Ctrl/⌘+V.");
                          } catch (e) {
                            toast.message("Couldn't auto-copy. Spec shown below — manually copy then paste in Emergent.");
                            window.prompt("Copy this spec, then paste into your Emergent chat:", ctx);
                          }
                          window.open("https://app.emergent.sh", "_blank", "noopener,noreferrer");
                        }}
                        className="w-full text-[11px] uppercase font-bold tracking-wide px-2 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded inline-flex items-center justify-center gap-1"
                        data-testid={`copy-change-request-${msg.change_request_id}`}
                      >
                        ↗ Copy spec & open Emergent build chat
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t-2 border-slate-200 bg-white p-3">
        <div className="flex gap-2 items-end">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask the assistant to update, toggle, report, or spec a new feature…"
            rows={2}
            disabled={loading}
            className="flex-1 border-2 border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:border-orange-400"
            data-testid="ai-prompt-input"
          />
          <button
            onClick={send}
            disabled={loading || !prompt.trim()}
            className="px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md flex items-center gap-2 text-sm font-semibold"
            data-testid="ai-send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[10px] text-slate-400 mt-1">Enter to send · Shift+Enter for newline · Claude Sonnet</div>
      </div>
    </div>
  );
};

// Dedicated tab page (full-screen)
export const CompanyAIAssistantPanel = ({ company, onCompanyUpdate }) => {
  if (!company) {
    return <div className="text-center py-20 text-slate-400">Open a company first to chat with its AI assistant.</div>;
  }
  return (
    <div className="bg-white rounded-md shadow border-2 border-slate-200 overflow-hidden flex flex-col" style={{ height: "min(calc(100dvh - 180px), 900px)", minHeight: "500px" }}>
      <div className="border-b-2 border-slate-200 p-4 flex items-center gap-3 bg-slate-900 text-white">
        <Sparkles className="w-5 h-5 text-orange-400" />
        <div>
          <div className="font-bold uppercase tracking-tight">{company.name}</div>
          <div className="text-xs text-slate-400 font-mono">{company.code} · AI Assistant</div>
        </div>
      </div>
      <AIChat
        companyId={company.id}
        companyName={company.name}
        companyCode={company.code}
        onCompanyUpdate={onCompanyUpdate}
      />
    </div>
  );
};

// Floating bubble (sits on every company-context page for super_admin)
export const CompanyAIAssistantBubble = ({ company, onCompanyUpdate }) => {
  const [open, setOpen] = useState(false);
  if (!company) return null;
  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
          data-testid="ai-bubble-open"
          title={`AI Assistant for ${company.name}`}
        >
          <Sparkles className="w-7 h-7" />
        </button>
      )}
      {open && (
        <div className="fixed inset-x-0 bottom-0 md:inset-auto md:bottom-6 md:right-6 z-50 md:w-[420px] md:h-[600px] h-[80vh] bg-white shadow-2xl md:rounded-md border-2 border-slate-200 flex flex-col overflow-hidden" data-testid="ai-bubble-panel">
          <div className="border-b-2 border-slate-200 p-3 flex items-center justify-between bg-slate-900 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-400" />
              <div>
                <div className="font-bold text-sm uppercase">{company.name}</div>
                <div className="text-[10px] text-slate-400 font-mono">{company.code} · AI Assistant</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="ai-bubble-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <AIChat
              companyId={company.id}
              companyName={company.name}
              companyCode={company.code}
              onCompanyUpdate={onCompanyUpdate}
            />
          </div>
        </div>
      )}
    </>
  );
};
